import React, { useState, useEffect } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getBrandColors} from "./ThemeContext";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };
const MONTHS     = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const HOURS      = [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];
const fmt12h     = h => h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h-12} PM`;

const cellKey = (dow, hour) => `${dow}-${hour}`;

const slotsToGrid = slots => {
  const g = new Set();
  (slots || []).forEach(s => {
    const startH = parseInt((s.start_time || "").split(":")[0], 10);
    const endH   = parseInt((s.end_time   || "").split(":")[0], 10);
    if (!isNaN(startH) && !isNaN(endH) && endH > startH) {
      for (let h = startH; h < endH; h++) g.add(cellKey(s.day_of_week, h));
    } else if (!isNaN(startH)) {
      g.add(cellKey(s.day_of_week, startH));
    }
  });
  return g;
};

function buildCalendar(year, month) {
  const first    = new Date(year, month - 1, 1);
  const last     = new Date(year, month, 0);
  const startDow = (first.getDay() + 6) % 7;
  const cells    = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++)
    cells.push({ date: d, dow: (new Date(year, month - 1, d).getDay() + 6) % 7 });
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// ── Read-only slot list ───────────────────────────────────────────────────────
function SlotPanel({ dow, dayLabel, grid, booked, T, textPrimary, textMuted }) {
  const onHours = HOURS.filter(h => grid.has(cellKey(dow, h)));
  const bkHours = HOURS.filter(h => booked.has(cellKey(dow, h)));

  if (onHours.length === 0 && bkHours.length === 0) {
    return (
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>{dayLabel}</div>
        <div style={{ fontSize: 12, color: textMuted, padding: "14px 0", textAlign: "center" }}>
          No sessions available on {dayLabel}s
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>
        {dayLabel} — Time Slots
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {HOURS.map(h => {
          const k        = cellKey(dow, h);
          const isBooked = booked.has(k);
          const isOn     = grid.has(k);
          if (!isOn && !isBooked) return null;

          let bg, color, badgeBg, badgeColor, badgeText;
          if (isBooked) {
            bg = "rgba(220,38,38,0.12)"; color = getBrandColors().red;
            badgeBg = "rgba(220,38,38,0.18)"; badgeColor = getBrandColors().red; badgeText = "Booked";
          } else {
            bg          = T ? (dow < 5 ? "rgba(3,78,123,0.35)" : "rgba(247,206,11,0.15)")
                            : (dow < 5 ? "rgba(3,78,123,0.08)" : "rgba(247,206,11,0.12)");
            color       = T ? (dow < 5 ? "#93c5fd" : C.yellow) : (dow < 5 ? C.blue : "#7a5f00");
            badgeBg     = T ? (dow < 5 ? "rgba(3,78,123,0.50)" : "rgba(247,206,11,0.25)")
                            : (dow < 5 ? "rgba(3,78,123,0.14)" : "rgba(247,206,11,0.30)");
            badgeColor  = color;
            badgeText   = "Available";
          }

          return (
            <div key={h} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 12px", borderRadius: 8, background: bg,
              border: `1px solid ${isBooked ? "rgba(220,38,38,0.20)" : "transparent"}` }}>
              <span style={{ fontSize: 12, fontWeight: 600, color }}>{fmt12h(h)} – {fmt12h(h + 1)}</span>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8,
                background: badgeBg, color: badgeColor, fontWeight: 700 }}>
                {badgeText}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Per-coach calendar ────────────────────────────────────────────────────────
function CoachCalendar({ coach, T, textPrimary, textMuted, borderColor, cardBg }) {
  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [selDow,   setSelDow]   = useState(null);
  const [selLabel, setSelLabel] = useState("");
  const [grid,     setGrid]     = useState(new Set());
  const [booked,   setBooked]   = useState(new Set());
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/oto/coaches/${coach.user_id}/availability`)
      .then(r => r.json())
      .then(d => {
        setGrid(slotsToGrid(d.availability || []));
        const bk = new Set();
        (d.booked_slots || []).forEach(({ day_of_week, hour }) => bk.add(cellKey(day_of_week, hour)));
        setBooked(bk);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [coach.user_id]);

  const prevMonth = () => { if (calMonth === 1) { setCalYear(y=>y-1); setCalMonth(12); } else setCalMonth(m=>m-1); setSelDow(null); };
  const nextMonth = () => { if (calMonth === 12) { setCalYear(y=>y+1); setCalMonth(1); } else setCalMonth(m=>m+1); setSelDow(null); };

  const weeks    = buildCalendar(calYear, calMonth);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;

  // Available dot color: dark mode uses light-blue / yellow; light mode uses the actual brand colors
  const dotWeekday = T ? "#93c5fd" : C.blue;
  const dotSat     = C.yellow;

  const dayHeaderColor = (i) => i === 5 ? C.yellow : i < 5 ? (T ? "#93c5fd" : C.blue) : textMuted;

  if (loading) return (
    <div style={{ padding: "32px 0", textAlign: "center", color: textMuted, fontSize: 13 }}>
      Loading availability…
    </div>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: selDow !== null ? "1fr 240px" : "1fr", gap: 14, alignItems: "start" }}>

      {/* Calendar card */}
      <div style={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 14, backdropFilter: "blur(10px)" , WebkitBackdropFilter: "blur(10px)"}}>

        {/* Month nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", borderBottom: `1px solid ${borderColor}` }}>
          <button onClick={prevMonth}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22,
              color: textPrimary, lineHeight: 1, padding: "0 6px" }}>‹</button>
          <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>
            {MONTHS[calMonth - 1]} {calYear}
          </span>
          <button onClick={nextMonth}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22,
              color: textPrimary, lineHeight: 1, padding: "0 6px" }}>›</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", padding: "10px 12px 2px" }}>
          {DAY_LABELS.map((d, i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 10, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.06em", color: dayHeaderColor(i) }}>
              {d}
            </div>
          ))}
        </div>

        {/* Week rows */}
        <div style={{ padding: "4px 12px 14px" }}>
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 4 }}>
              {week.map((cell, ci) => {
                if (!cell) return <div key={ci}/>;
                const { date, dow } = cell;
                const dateStr  = `${calYear}-${String(calMonth).padStart(2,"0")}-${String(date).padStart(2,"0")}`;
                const isToday  = dateStr === todayStr;
                const isPast   = dateStr < todayStr;
                const isSel    = selDow === dow;
                const onHours  = HOURS.filter(h => grid.has(cellKey(dow, h)));
                const bkHours  = HOURS.filter(h => booked.has(cellKey(dow, h)));
                const hasSlots = onHours.length > 0 || bkHours.length > 0;

                return (
                  <div key={ci}
                    onClick={() => {
                      if (!hasSlots) return;
                      setSelDow(prev => prev === dow ? null : dow);
                      setSelLabel(DAY_LABELS[dow]);
                    }}
                    style={{
                      borderRadius: 9, padding: "6px 3px 5px", textAlign: "center",
                      cursor: hasSlots ? "pointer" : "default",
                      border: isSel
                        ? `2px solid ${C.yellow}`
                        : `1px solid ${borderColor}`,
                      background: isSel
                        ? (T ? "rgba(247,206,11,0.12)" : "rgba(247,206,11,0.14)")
                        : isToday
                        ? (T ? "rgba(247,206,11,0.10)" : "rgba(247,206,11,0.14)")
                        : "transparent",
                      opacity: isPast ? 0.35 : 1,
                      transition: "all 0.12s",
                      minHeight: 60,
                    }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 900 : 600,
                      color: isToday ? C.yellow : isSel ? C.yellow : textPrimary }}>
                      {date}
                    </div>
                    {isToday && (
                      <div style={{ fontSize: 7, color: C.yellow, fontWeight: 800, letterSpacing: "0.04em" }}>TODAY</div>
                    )}

                    {/* Booked dots */}
                    {bkHours.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2, margin: "3px 0 2px" }}>
                        {bkHours.slice(0, 3).map(h => (
                          <div key={h} title={`${fmt12h(h)} – booked`}
                            style={{ width: 5, height: 5, borderRadius: "50%", background: getBrandColors().red }}/>
                        ))}
                        {bkHours.length > 3 && <span style={{ fontSize: 7, color: getBrandColors().red }}>+{bkHours.length-3}</span>}
                      </div>
                    )}

                    {/* Available dots */}
                    {onHours.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
                        {onHours.slice(0, 4).map(h => (
                          <div key={h} title={fmt12h(h)}
                            style={{ width: 5, height: 5, borderRadius: "50%",
                              background: dow < 5 ? dotWeekday : dotSat }}/>
                        ))}
                        {onHours.length > 4 && (
                          <span style={{ fontSize: 7, color: dow < 5 ? dotWeekday : dotSat }}>
                            +{onHours.length - 4}
                          </span>
                        )}
                      </div>
                    )}

                    {!isPast && !hasSlots && dow !== 6 && (
                      <div style={{ fontSize: 8, color: textMuted, marginTop: 3 }}>off</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 14, padding: "8px 16px",
          borderTop: `1px solid ${borderColor}`, flexWrap: "wrap" }}>
          {[
            { color: dotWeekday, label: "Available (weekday)" },
            { color: dotSat,     label: "Available (Sat)" },
            { color: getBrandColors().red,  label: "Already booked" },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: color }}/>
              <span style={{ fontSize: 10, color: textMuted }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Slot detail panel */}
      {selDow !== null && (
        <div style={{ background: cardBg, border: `1px solid ${borderColor}`,
          borderRadius: 14, backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", padding: "14px 16px" }}>
          <SlotPanel dow={selDow} dayLabel={selLabel} grid={grid} booked={booked}
            T={T} textPrimary={textPrimary} textMuted={textMuted} />
        </div>
      )}
    </div>
  );
}

// ── Top-level exported component ──────────────────────────────────────────────
export default function CoachAvailabilityView() {
  const { isDark: T } = useTheme();

  // Theme-aware palette
  const textPrimary = T ? "rgba(255,255,255,0.90)" : "#1a2a3a";
  const textMuted   = T ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.55)";
  const borderColor = T ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.12)";
  const cardBg      = T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)";
  const coachBtnBg  = (active) => active ? C.yellow : (T ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)");
  const coachBtnClr = (active) => active ? "#1a1200" : textPrimary;

  const [coaches,  setCoaches]  = useState([]);
  const [selCoach, setSelCoach] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/oto/coaches`)
      .then(r => r.json())
      .then(d => { const list = d.coaches || []; setCoaches(list); if (list.length) setSelCoach(list[0]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary,
      padding: "24px 28px", maxWidth: 920 }}>

      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: T ? "#fff" : C.blue, letterSpacing: "-0.01em" }}>
          Coach 1-on-1 Availability
        </div>
        <div style={{ fontSize: 13, color: textMuted, marginTop: 4 }}>
          View each coach's available slots. Click any day to see the hour breakdown.
        </div>
      </div>

      {loading && <div style={{ color: textMuted, fontSize: 13, padding: "32px 0" }}>Loading coaches…</div>}
      {!loading && coaches.length === 0 && (
        <div style={{ color: textMuted, fontSize: 13, padding: "32px 0" }}>No coaches found.</div>
      )}

      {!loading && coaches.length > 0 && (
        <>
          {/* Coach selector — with rate shown inline */}
          {coaches.length > 1 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
              {coaches.map(c => {
                const active = selCoach?.user_id === c.user_id;
                return (
                  <button key={c.user_id} onClick={() => setSelCoach(c)}
                    style={{ padding: "8px 16px", borderRadius: 12, border: `1px solid ${active ? C.yellow : borderColor}`,
                      cursor: "pointer", fontSize: 13, fontWeight: 700, transition: "all 0.14s", textAlign: "left",
                      background: coachBtnBg(active), color: coachBtnClr(active) }}>
                    <div>{c.full_name}</div>
                    {c.hourly_rate > 0 && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: active ? "#7a5f00" : C.yellow, marginTop: 2 }}>
                        ${Number(c.hourly_rate).toFixed(2)}/hr
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Single coach (no selector needed) — show name + rate as a small header */}
          {coaches.length === 1 && selCoach && (
            <div style={{ marginBottom: 14, display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{selCoach.full_name}</span>
              {selCoach.hourly_rate > 0 && (
                <span style={{ fontSize: 13, fontWeight: 700, color: C.yellow }}>
                  ${Number(selCoach.hourly_rate).toFixed(2)}/hr
                </span>
              )}
            </div>
          )}

          {/* Calendar */}
          {selCoach && (
            <CoachCalendar key={selCoach.user_id} coach={selCoach}
              T={T} textPrimary={textPrimary} textMuted={textMuted}
              borderColor={borderColor} cardBg={cardBg} />
          )}
        </>
      )}
    </div>
  );
}
